import { useEffect, type ReactElement } from "react";
import {
  Badge,
  Banner,
  BodyText,
  Button,
  Card,
  Flex,
  Heading,
  Modal,
  Stack,
} from "@knowtex-ai/components";
import type { TelehealthAudioState } from "./types";

export interface TelehealthSetupModalProps {
  open: boolean;
  audioState: TelehealthAudioState;
  onClose: () => void;
  onShareScreenAudio: () => void;
  /** The tutorial card sits outside the modal, so clicking it must not close the modal. */
  closeOnPressOutside?: boolean;
}

const TUTORIAL_CLOSE_ID = "telehealth_close_modal_x";

export default function TelehealthSetupModal({
  open,
  audioState,
  onClose,
  onShareScreenAudio,
  closeOnPressOutside = true,
}: TelehealthSetupModalProps): ReactElement {
  const isReady = audioState.hasMicrophone && audioState.hasScreenAudio;

  useEffect(() => {
    if (!open) {
      return;
    }

    const dialog = document.querySelector('[role="dialog"]');
    const closeButton = dialog?.querySelector<HTMLButtonElement>('button[aria-label="Close"]');
    if (closeButton) {
      closeButton.id = TUTORIAL_CLOSE_ID;
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
      title="Telehealth Audio Setup"
      subtitle="Connect your microphone and the video-call tab before you start recording."
      size="medium"
      className="telehealth-setup-modal"
      closeOnPressOutside={closeOnPressOutside}
    >
      <Stack gap={16}>
        <div id="telehealth_microphone_step">
          <Card>
            <Stack gap={12}>
              <StepHeader
                title="Step 1. Microphone"
                description="This connects automatically for telehealth visits."
                isConnected={audioState.hasMicrophone}
              />
              {!audioState.hasMicrophone ? (
                <Banner variant="warning" heading="Microphone not detected">
                  Reload the page or check browser permissions, then try telehealth again.
                </Banner>
              ) : null}
            </Stack>
          </Card>
        </div>

        <div id="telehealth_screen_audio_step">
          <Card>
            <Stack gap={12}>
              <StepHeader
                title="Step 2. Call audio"
                description="Share the browser tab or window with your video call (Zoom, Teams, or Meet)."
                isConnected={audioState.hasScreenAudio}
              />
              <Banner variant="warning" heading="Share audio">
                <ol className="ml-4 list-decimal space-y-1">
                  <li>Click Share Screen Audio</li>
                  <li>Select the tab with your video call</li>
                  <li>
                    Check <strong>Share audio</strong> at the bottom
                  </li>
                  <li>Click Share</li>
                </ol>
              </Banner>
              <Button
                type="button"
                variant="primary"
                fullWidth
                disabled={audioState.hasScreenAudio}
                aria-label={
                  audioState.hasScreenAudio ? "Screen audio connected" : "Share screen audio"
                }
                onClick={onShareScreenAudio}
              >
                {audioState.hasScreenAudio ? "Screen Audio Connected" : "Share Screen Audio"}
              </Button>
            </Stack>
          </Card>
        </div>

        {isReady ? (
          <Stack gap={12}>
            <Banner variant="success" heading="All Set!">
              Both audio sources are connected. You&apos;re ready to start recording.
            </Banner>
            <Button type="button" variant="primary" fullWidth aria-label="Done" onClick={onClose}>
              Done
            </Button>
          </Stack>
        ) : null}
      </Stack>
    </Modal>
  );
}

function StepHeader({
  title,
  description,
  isConnected,
}: {
  title: string;
  description: string;
  isConnected: boolean;
}): ReactElement {
  return (
    <Flex alignItems="flex-start" justifyContent="space-between" gap={12}>
      <div className="min-w-0">
        <Heading as="h3" size="x-small">
          {title}
        </Heading>
        <BodyText size="small" muted>
          {description}
        </BodyText>
      </div>
      {isConnected ? (
        <Badge variant="success" showIcon size="small" className="shrink-0">
          Connected
        </Badge>
      ) : null}
    </Flex>
  );
}
